import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Inventory2 as InventoryIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import MainTitle from "../../components/mainTitle";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import { CONSTANTS } from "../../../utils/CONSTS";
import { pages } from "../../../utils/common";
import { useInstituteContext } from "../../../context/institute.context";
import { useMaterialGroupListHook } from "../../../hooks/useMaterialHook";
import {
  formatLabel,
  materialCategoryOptions,
  materialStatusOptions,
  renderMaterialStatusChip,
} from "./material.helpers";
import { MaterialViewProps } from "./material.interfaces";
import ManageMaterialGroupDialog from "./manageMaterialGroupDialog";
import { TMaterialGroup } from "../../../apis/material/material.interfaces";

const PAGE_SIZE = 20;

const MaterialView: FC<MaterialViewProps> = () => {
  const navigate = useNavigate();
  const { selectedInstitute } = useInstituteContext();

  const [page, setPage] = useState(1);
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<TMaterialGroup | null>(null);

  const { control, watch } = useForm({ defaultValues: { search: "" } });
  const searchQuery = watch("search");

  const filters = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      category: categoryFilter || undefined,
      status: statusFilter || undefined,
      search: searchQuery || undefined,
    }),
    [page, categoryFilter, statusFilter, searchQuery],
  );

  const { data, isLoading, error } = useMaterialGroupListHook(filters);
  const groups = data?.data ?? [];
  const totalCount = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const openCreate = () => {
    setEditingGroup(null);
    setDialogOpen(true);
  };
  const openEdit = (group: TMaterialGroup) => {
    setEditingGroup(group);
    setDialogOpen(true);
  };
  const closeDialog = () => {
    setDialogOpen(false);
    setEditingGroup(null);
  };

  if (!selectedInstitute) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.material} />
        <Box p={3} data-testid="material-view">
          <MainTitle title="Materials" />
          <Alert severity="info" data-testid="material-no-institute">
            Please select an institute from the dashboard to view materials.
          </Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <DocumentTitle title={CONSTANTS.title.material} />
      <Grid container spacing={3} data-testid="material-view">
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <MainTitle title="Materials" />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              size="large"
              onClick={openCreate}
              data-testid="add-material-group-button"
            >
              Add Material Group
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Card data-testid="material-filters-card">
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={5}>
                  <ControlledTextField
                    control={control}
                    name="search"
                    id="material-search"
                    label="Search"
                    isGridItem={false}
                    fullWidth
                    placeholder="Search by name, department, location..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{ "data-testid": "material-search-input" }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Select
                    fullWidth
                    size="small"
                    displayEmpty
                    value={categoryFilter}
                    onChange={(e) => {
                      setCategoryFilter(e.target.value);
                      setPage(1);
                    }}
                    data-testid="material-category-filter"
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {materialCategoryOptions.map((c) => (
                      <MenuItem key={c} value={c}>
                        {formatLabel(c)}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Select
                    fullWidth
                    size="small"
                    displayEmpty
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                    }}
                    data-testid="material-status-filter"
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    {materialStatusOptions.map((s) => (
                      <MenuItem key={s} value={s}>
                        {formatLabel(s)}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {isLoading ? (
          <Grid item xs={12}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="400px"
              data-testid="material-loading"
            >
              <CircularProgress />
            </Box>
          </Grid>
        ) : error ? (
          <Grid item xs={12}>
            <Alert severity="error" data-testid="material-error">
              Failed to load materials. Please try again later.
            </Alert>
          </Grid>
        ) : groups.length === 0 ? (
          <Grid item xs={12}>
            <Card data-testid="material-empty-state">
              <CardContent>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="200px"
                  gap={2}
                >
                  <InventoryIcon
                    sx={{ fontSize: 64, color: "text.secondary" }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    {totalCount === 0
                      ? "No material groups configured"
                      : "No material groups match your filters"}
                  </Typography>
                  {totalCount === 0 && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={openCreate}
                      data-testid="add-material-group-empty-button"
                    >
                      Add Material Group
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          <>
            <Grid item xs={12}>
              <Typography
                variant="h6"
                gutterBottom
                data-testid="material-count"
              >
                Material Groups ({totalCount})
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TableContainer
                component={Paper}
                data-testid="material-table-container"
              >
                <Table data-testid="material-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groups.map((group) => (
                      <TableRow
                        key={group.id}
                        hover
                        data-testid={`material-row-${group.id}`}
                      >
                        <TableCell>
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {group.name}
                            </Typography>
                            {group.tags && group.tags.length > 0 && (
                              <Box
                                display="flex"
                                gap={0.5}
                                flexWrap="wrap"
                                sx={{ mt: 0.5 }}
                              >
                                {group.tags.map((tag) => (
                                  <Chip
                                    key={tag}
                                    label={tag}
                                    size="small"
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{formatLabel(group.category)}</TableCell>
                        <TableCell>{group.department ?? "—"}</TableCell>
                        <TableCell>{group.location ?? "—"}</TableCell>
                        <TableCell align="right">{group.quantity}</TableCell>
                        <TableCell>
                          {renderMaterialStatusChip(group.status)}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() =>
                              navigate(`${pages.material.path}/${group.id}`)
                            }
                            data-testid={`view-material-${group.id}`}
                          >
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => openEdit(group)}
                            data-testid={`edit-material-${group.id}`}
                          >
                            <EditIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            {totalPages > 1 && (
              <Grid item xs={12}>
                <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                    data-testid="material-pagination"
                  />
                </Box>
              </Grid>
            )}
          </>
        )}
      </Grid>

      <ManageMaterialGroupDialog
        open={dialogOpen}
        onClose={closeDialog}
        group={editingGroup}
      />
    </>
  );
};

export default MaterialView;
